<template>
  <div class="lf-animated-headline">
    <component :is="state.type ?? 'h2'" class="lf-animated-headline__content" :style="headlineStyles">
      <span v-if="state.textBefore" class="lf-animated-headline__content-before">
        <ApplyReplacementTags :html="state.textBefore" :extra-replacement-tags="model.column?.replacementTags" />
      </span>
      <span
        class="lf-animated-headline__content-animated"
        :class="[
          state.style,
          {
            'lf-animated-headline__content-animated--has-before': state.textBefore,
            'lf-animated-headline__content-animated--has-after': state.textAfter
          }
        ]"
        :style="model.state.elementStyling?.animatedTextIfGradient"
      >
        <LfAnimatedHeadlineRotation
          v-if="state.hasRotation"
          :animation="state.animation"
          :rotate-text-color="state.settings?.layout?.typography?.rotateTextColor"
          :gradient-color="state?.elementStyling?.gradientText || {}"
          :lines="state.rotatingTextArray ? state.rotatingTextArray : []"
          :is-full-width="state.style === AnimatedHeadlineStyleType.ROTATION_ONLY_TEXT"
        />

        <span
          v-if="state.style === AnimatedHeadlineStyleType.HIGHLIGHTED && state.highlighted"
          class="lf-animated-headline__content-animated-active"
        >
          <ApplyReplacementTags :html="state.highlighted" :extra-replacement-tags="model.column?.replacementTags" />
        </span>
        <SVGShape v-if="state.style === AnimatedHeadlineStyleType.HIGHLIGHTED && showAnimation" :state="state" />
      </span>
      <span v-if="state.textAfter" class="lf-animated-headline__content-after">
        <ApplyReplacementTags :html="state.textAfter" :extra-replacement-tags="model.column?.replacementTags" />
      </span>
    </component>
  </div>
</template>

<script lang="ts">
import type { CSSProperties, PropType } from 'vue';
import { computed, defineComponent, onMounted } from 'vue';
import type { AddonLfAnimatedHeadlineModel } from '@/src/components/addons/lf-animated-headline/Model';
import LfAnimatedHeadlineRotation from '@/src/components/addons/lf-animated-headline/Rotation/LfAnimatedHeadlineRotation.vue';
import { AnimatedHeadlineStyleType } from '@/src/typings/enums/enums';
import SVGShape from '@/src/components/addons/lf-animated-headline/SVGShape.vue';
import useFontObserver from '@/src/hooks/useFontObserver';
import ApplyReplacementTags from '@/src/components/ApplyReplacementTags.vue';

export default defineComponent({
  name: 'AddonLfAnimatedHeadline',
  components: {
    ApplyReplacementTags,
    SVGShape,
    LfAnimatedHeadlineRotation
  },
  inheritAttrs: false,
  props: {
    model: {
      type: Object as PropType<AddonLfAnimatedHeadlineModel>,
      required: true
    }
  },
  setup(props) {
    const state = (props.model as AddonLfAnimatedHeadlineModel).state;
    const showAnimation = computed(() => {
      return !!(state.highlighted && state.highlighted !== '');
    });

    const headlineStyles = computed<CSSProperties>(() => {
      const headlineInlineStyle = state.elementStyling?.text;
      const gradientInlineStyle = state.elementStyling?.gradientText || '';

      return {
        ...gradientInlineStyle,
        ...headlineInlineStyle
      };
    });

    let readyPromiseResolve: (() => void) | undefined;
    const readyPromise = new Promise<void>((resolve) => {
      readyPromiseResolve = resolve;
    });

    onMounted(async () => {
      const { isFontLoaded } = useFontObserver();

      if (state.settings?.layout?.typography?.fontFamily) {
        await isFontLoaded(state.settings.layout.typography.fontFamily);
      }
      if (readyPromiseResolve) {
        readyPromiseResolve();
      }
    });

    return {
      state,
      headlineStyles,
      showAnimation,
      AnimatedHeadlineStyleType,
      onBeforeEnter: async () => {
        await readyPromise;
      }
    };
  }
});
</script>

<style lang="scss">
.lf-animated-headline__content {
  &-rotation {
    display: inline-block;
    perspective: 300px;
    vertical-align: top;
    max-width: 100%;

    &--full-width {
      .lf-animated-headline__content-rotation-line {
        width: 100%;
      }
    }

    &-line {
      display: block;
      position: absolute;
      left: 0;
      top: 0;
      animation-duration: 800ms;

      &--flip {
        &-in {
          animation: animated-headline-flip-in 800ms;
          animation-fill-mode: forwards;
          transform-origin: 50% 100%;
          transform: rotateX(180deg);
        }

        &-out {
          animation: animated-headline-flip-out 800ms;
          animation-fill-mode: forwards;
          transform-origin: 50% 100%;
          transform: rotateX(180deg);
        }
      }

      &--dropin {
        &-in {
          animation: animated-headline-dropin-in 800ms;
          animation-fill-mode: forwards;
        }

        &-out {
          animation: animated-headline-dropin-out 800ms;
          animation-fill-mode: forwards;
        }
      }
    }
  }

  &-animated {
    display: inline-block;
    position: relative;
    vertical-align: top;
    max-width: 100%;

    &::before {
      content: '';
    }

    &::after {
      content: '';
    }

    &--has-before::before {
      content: '\00a0';
    }

    &--has-after::after {
      content: '\00a0';
    }

    &.highlighted {
      &::before {
        content: '\00a0';
      }

      &::after {
        content: '\00a0';
      }
    }

    svg {
      position: absolute;
      top: 50%;
      left: 50%;
      width: calc(100% + 20px);
      height: calc(100% + 20px);
      transform: translate(-50%, -50%);
      overflow: visible;

      path {
        stroke: black;
        stroke-width: 2;
        fill: none;
        stroke-dasharray: 1500;
        opacity: 0;
        animation: headline-dash 10s infinite 1.5s;
      }
    }
  }
}

@keyframes headline-dash {
  0% {
    opacity: 1;
    stroke-dasharray: 0 1500;
  }
  15% {
    stroke-dasharray: 1500 1500;
  }
  85% {
    opacity: 1;
  }
  90% {
    stroke-dasharray: 1500 1500;
    opacity: 0;
  }
  to {
    stroke-dasharray: 0 1500;
    opacity: 0;
  }
}

@keyframes animated-headline-dropin-in {
  0% {
    opacity: 0;
    transform: translateZ(100px);
  }
  to {
    opacity: 1;
    transform: translateZ(0);
  }
}

@keyframes animated-headline-dropin-out {
  0% {
    opacity: 1;
    transform: translateZ(0);
  }

  to {
    opacity: 0;
    transform: translateZ(-100px);
  }
}

@keyframes animated-headline-flip-in {
  0% {
    transform: rotateX(180deg);
    opacity: 0;
  }

  35% {
    transform: rotateX(120deg);
    opacity: 0;
  }

  65% {
    opacity: 0;
  }

  to {
    transform: rotateX(1turn);
    opacity: 1;
  }
}

@keyframes animated-headline-flip-out {
  0% {
    transform: rotateX(0deg);
    opacity: 1;
  }

  35% {
    transform: rotateX(-40deg);
    opacity: 1;
  }

  65% {
    opacity: 0;
  }

  to {
    transform: rotateX(180deg);
    opacity: 0;
  }
}
</style>
